import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
export default function DrawnSignature({
  value,
  onChange,
  disabled = false,
  label = "Draw your signature",
}) {
  const canvas = useRef(null),
    drawing = useRef(false),
    ink = useRef(false),
    pointer = useRef(null);
  useEffect(() => {
    if (!value) {
      canvas.current?.getContext("2d")?.clearRect(0, 0, 720, 240);
      ink.current = false;
    }
  }, [value]);
  const point = (event) => {
    const rect = canvas.current.getBoundingClientRect();
    return [
      ((event.clientX - rect.left) * 720) / rect.width,
      ((event.clientY - rect.top) * 240) / rect.height,
    ];
  };
  const finish = (event) => {
    if (!drawing.current || (event && pointer.current !== event.pointerId))
      return;
    drawing.current = false;
    if (ink.current) onChange(canvas.current.toDataURL("image/png"));
  };
  return (
    <fieldset className="rcti-signature-pad" disabled={disabled}>
      <legend>{label}</legend>
      <p>Sign with your mouse, finger or stylus.</p>
      <canvas
        ref={canvas}
        width={720}
        height={240}
        aria-label={label}
        role="img"
        aria-disabled={disabled}
        onPointerDown={(event) => {
          if (
            disabled ||
            drawing.current ||
            (event.button !== undefined && event.button !== 0)
          )
            return;
          const ctx = canvas.current.getContext("2d");
          if (!ctx) return;
          const [x, y] = point(event);
          drawing.current = true;
          pointer.current = event.pointerId;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.lineWidth = 3;
          ctx.strokeStyle =
            getComputedStyle(canvas.current)
              .getPropertyValue("--neutral-900")
              .trim() || "#142624";
          canvas.current.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (
            !drawing.current ||
            disabled ||
            pointer.current !== event.pointerId
          )
            return;
          const ctx = canvas.current.getContext("2d");
          ctx.lineTo(...point(event));
          ctx.stroke();
          ink.current = true;
        }}
        onPointerUp={finish}
        onPointerCancel={finish}
        onLostPointerCapture={finish}
      />
      <button
        type="button"
        className="rcti-button-secondary"
        disabled={disabled || !value}
        onClick={() => {
          canvas.current.getContext("2d").clearRect(0, 0, 720, 240);
          drawing.current = false;
          ink.current = false;
          onChange("");
        }}
      >
        Clear signature
      </button>
    </fieldset>
  );
}
DrawnSignature.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
};
export function SavedSignature({ value, label }) {
  if (!value) return null;
  return value.startsWith("data:image/png;base64,") ? (
    <figure className="rcti-saved-signature">
      <figcaption>{label}</figcaption>
      <img src={value} alt={label} />
    </figure>
  ) : (
    <p>
      {label}: {value}
    </p>
  );
}
SavedSignature.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string.isRequired,
};
